import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewContainerRef } from '@angular/core';
import { ConnectionPositionPair, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { BehaviorSubject, EMPTY, fromEvent, merge, Observable, Subject } from 'rxjs';
import { auditTime, distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { PhxDropdownComponent } from './phx-dropdown.component';
import { POSITION_MAP, POSITION_TYPE } from './overlay-position';
import { PhxDropdownOffset } from './phx-dropdown.types';
import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';

const listOfPositions: ConnectionPositionPair[] = [POSITION_MAP.bottomLeft, POSITION_MAP.bottomRight, POSITION_MAP.topLeft, POSITION_MAP.topRight, POSITION_MAP.cover];

type PhxTriggerType = 'click' | null;

@Directive({
  selector: '[phxDropdownFor]',
  exportAs: 'phxDropdownFor',
  host: {
    class: 'dropdown-trigger',
    '(keydown)': 'onKeydown($event)',
  },
})
export class PhxDropdownDirective implements AfterViewInit, OnDestroy, OnChanges {
  @Input() public phxDropdownFor: PhxDropdownComponent | null = null;
  @Input() public phxTrigger: PhxTriggerType = 'click';
  @Input() public phxPosition: POSITION_TYPE = 'bottomLeft';
  @Input() public phxClickHide = true;
  @Input() public phxVisible = false;
  @Input() public phxDisabled = false;
  @Input() public phxBackdrop = false;
  @Input() public phxDropdownClassName: string = '';
  @Input() public phxDropdownWidth: number | string = 'auto';
  @Input() public phxDropdownHeight: number | string = 'auto';
  @Input() public phxDropdownOffset: PhxDropdownOffset = { x: 0, y: 0 };
  @Output() public phxVisibleChange: EventEmitter<boolean> = new EventEmitter();
  @Output() public phxBackdropClicked: EventEmitter<void> = new EventEmitter();
  private portal: TemplatePortal;
  private overlayRef: OverlayRef | null = null;
  private positionStrategy = this.overlay.position().flexibleConnectedTo(this.nativeElement).withPositions(listOfPositions);

  private phxDropdownWidth$ = new BehaviorSubject<number | string>(this.phxDropdownWidth);
  private phxDropdownHeight$ = new BehaviorSubject<number | string>(this.phxDropdownHeight);
  private phxTrigger$: BehaviorSubject<PhxTriggerType> = new BehaviorSubject<PhxTriggerType>('click');
  private phxVisible$ = new BehaviorSubject<boolean>(false);
  private phxDisabled$ = new BehaviorSubject<boolean>(false);
  private overlayBackdrop$ = new Subject<boolean>();
  private dropdownClassName$ = new BehaviorSubject<{ currentValue: string; previousValue: string }>({
    currentValue: '',
    previousValue: '',
  });
  private destroy$: Subject<void> = new Subject<void>();

  private createOverlay(): OverlayRef {
    const overlayRef = this.overlay.create({
      hasBackdrop: this.phxTrigger === 'click' || this.phxBackdrop,
      backdropClass: 'phx-overlay-backdrop-transparent',
      positionStrategy: this.positionStrategy,
      disposeOnNavigation: true,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
    return overlayRef;
  }

  private createAndAttachOverlay() {
    if (this.phxVisible) {
      if (!this.overlayRef) {
        this.overlayRef = this.createOverlay();
        this.detachmentStats$.subscribe(() => {
          this.overlayBackdrop$.next(false);
        });

        this.dropdownClassName$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
          this.overlayRef.removePanelClass(value.previousValue?.split(" "))
          this.overlayRef.addPanelClass(value.currentValue?.split(" "));
        });
      }

      const position: ConnectionPositionPair = POSITION_MAP[this.phxPosition];
      position.offsetX = this.phxDropdownOffset.x || 0;
      position.offsetY = this.phxDropdownOffset.y || 0;
      this.positionStrategy.withPositions([position, ...listOfPositions]);

      if (!this.portal || this.portal.templateRef !== this.phxDropdownFor.templateRef) {
        this.portal = new TemplatePortal(this.phxDropdownFor.templateRef, this.viewContainerRef);
      }
      this.overlayRef.attach(this.portal);

      // Add position-specific classes
      if (this.phxPosition === 'cover') {
        this.overlayRef.addPanelClass('phx-dropdown-position-cover');
      } else {
        this.overlayRef.removePanelClass('phx-dropdown-position-cover');
      }

      // TODO
      // This is a temporary fix to animate opening of dropdown panel to stop flicker of scrollbar.
      // This will be fixed in this ticket https://k2labs.atlassian.net/browse/FP-912
      this.overlayRef.addPanelClass('cdk-overlay-animation-start');
      setTimeout(() => {
        this.overlayRef.addPanelClass('cdk-overlay-animation-end');
      }, 100);

      this.overlayRef
        .backdropClick()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.phxBackdropClicked.emit();
        });
    } else {
      if (this.overlayRef) {
        this.overlayRef.detach();
        this.overlayRef.removePanelClass('cdk-overlay-animation-end');
        this.overlayRef.removePanelClass('phx-dropdown-position-cover');
      }
    }
  }

  private get clickStates$(): Observable<boolean> {
    const hostClickState = fromEvent(this.nativeElement, 'click').pipe(map(() => !this.phxVisible));
    // TODO: Decide if we should bake in below keyboard a11y for click states as well
    // const hostKeyboardPressState = fromEvent(this.nativeElement, 'keyup').pipe(
    //   filter((event: KeyboardEvent) => event.code === 'Space' || event.code === 'Enter' ),
    //   map(() => !this.phxVisible));
    // return merge(hostClickState, hostKeyboardPressState, this.overlayBackdrop$);
    return merge(hostClickState, this.overlayBackdrop$);
  }

  private get visibleStateByTrigger$(): Observable<boolean> {
    const visibleState$ = this.phxTrigger$.pipe(
      switchMap((trigger) => {
        if (trigger === 'click') {
          return this.clickStates$;
        } else {
          return EMPTY;
        }
      })
    );
    return visibleState$;
  }

  private get detachmentStats$(): Observable<boolean | void | MouseEvent> {
    return merge(this.overlayRef.backdropClick(), this.overlayRef.detachments()).pipe(takeUntil(this.destroy$));
  }

  private get nativeElement(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  constructor(public elementRef: ElementRef, private viewContainerRef: ViewContainerRef, private overlay: Overlay) { }



  ngOnChanges(simpleChanges: SimpleChanges): void {
    const { phxTrigger, phxVisible, phxDisabled, phxDropdownClassName, phxDropdownWidth, phxDropdownHeight } = simpleChanges;
    if (phxTrigger) {
      this.phxTrigger$.next(this.phxTrigger);
    }
    if (phxVisible) {
      this.phxVisible$.next(this.phxVisible);
    }
    if (phxDisabled) {
      this.phxDisabled$.next(this.phxDisabled);
    }
    if (phxDropdownClassName) {
      this.dropdownClassName$.next({
        currentValue: phxDropdownClassName.currentValue,
        previousValue: phxDropdownClassName.previousValue,
      });
    }
    if (phxDropdownWidth) this.phxDropdownWidth$.next(this.phxDropdownWidth);
    if (phxDropdownHeight) this.phxDropdownHeight$.next(this.phxDropdownHeight);
  }

  ngAfterViewInit(): void {
    if (this.phxDropdownFor) {
      const menuItemsClick$ = this.phxDropdownFor.descendantMenuItemClick.pipe(map(() => false));
      const menuItemClickClose$ = this.phxDropdownFor.toggle$.pipe(filter(() => this.phxClickHide));

      this.phxDropdownWidth$.pipe(takeUntil(this.destroy$)).subscribe((value) => this.overlayRef?.updateSize({ width: value }));

      this.phxDropdownHeight$.pipe(takeUntil(this.destroy$)).subscribe((value) => this.overlayRef?.updateSize({ maxHeight: value }));

      merge(menuItemsClick$, this.visibleStateByTrigger$, menuItemClickClose$, this.phxVisible$, this.phxDisabled$)
        .pipe(
          auditTime(50),
          distinctUntilChanged(),
          filter(() => !this.phxDisabled),
          takeUntil(this.destroy$)
        )
        .subscribe((visible) => {
          if (this.phxVisible !== visible) {
            this.phxVisibleChange.emit(visible);
          }
          this.phxVisible = visible;
          this.createAndAttachOverlay();
          this.phxDropdownWidth$.next(this.phxDropdownWidth);
          this.phxDropdownHeight$.next(this.phxDropdownHeight);
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  public updateOverlayPosition(): void {
    if (this.overlayRef) {
      setTimeout(() => this.overlayRef.updatePosition());
    }
  }

  public onKeydown(event: KeyboardEvent): void {
    if(this.phxVisible && this.phxDropdownFor){
      switch (event.key) {
        case KeyboardKeys.ESC:
          this.phxVisible$.next(false);
          event.preventDefault();
          break;
        case KeyboardKeys.ARROW_DOWN:
          this.phxDropdownFor.focusFirstMenuItem();
          event.preventDefault();
          break;
      }
    }
  }
}